export class ObjectColor {
    public foreground: string;
    public background: string;
    public border: string;

    constructor(foreground: string, background: string, border: string) {
        this.foreground = foreground;
        this.background = background;
        this.border = border;
    }
}

/**
 * Contains a list of all the bootstrap -500 colors
 */
export const colorMap = ['#0D6EFD', '#6610F2', '#6F42C1', '#D63384', '#DC3545', '#FD7E14', '#FFC107', '#198754', '#20C997', '#0DCAF0'];

export const colorMapSecondary = [
    '#6610F2',
    '#6F42C1',
    '#D63384',
    '#DC3545',
    '#FD7E14',
    '#FFC107',
    '#198754',
    '#20C997',
    '#0DCAF0',
    '#6610F2',
    '#6F42C1',
    '#D63384',
    '#DC3545',
    '#FD7E14',
    '#FFC107',
    '#198754',
    '#20C997',
    '#0DCAF0',
];

/**
 * Contains all the opacity hex values
 */
const colorOpacityMap = new Map<0 | 25 | 50 | 75 | 90 | 100, string>([
    [0, '00'],
    [25, '40'],
    [50, '80'],
    [75, 'bf'],
    [90, 'e6'],
    [100, 'ff'],
]);

// Setup a map with the satmin satmax lightmin lightmax for each type
const typeMap = new Map<string, { satmin: number; satmax: number; lightmin: number; lightmax: number }>([
    ['all', { satmin: 0, satmax: 100, lightmin: 0, lightmax: 100 }],
    ['dark', { satmin: 70, satmax: 100, lightmin: 25, lightmax: 50 }],
    ['light', { satmin: 70, satmax: 100, lightmin: 70, lightmax: 100 }],
    ['nogray', { satmin: 0, satmax: 100, lightmin: 70, lightmax: 90 }],
]);

/**
 * Will return a random color based on string as seed
 * This mean it is deterministic and will always return the same color for the same string
 * @param str
 * @param type all, dark, light, nogray
 * @param opacitypct 0, 25, 50, 75, 90, 100
 * @returns ObjectColor with foreground, background and border color
 */
export const getColorByString = (
    str: string,
    type: 'all' | 'dark' | 'light' | 'nogray',
    opacitypct?: 0 | 25 | 50 | 75 | 90 | 100,
): ObjectColor => {
    const typeObj = typeMap.get(type);

    opacitypct = opacitypct ?? 100;

    const strHash = getHashOfString(str);
    const h = normalizeHash(strHash, 0, 360);
    const s = normalizeHash(strHash, typeObj!.satmin, typeObj!.satmax); // with a minimum of 50 to make sure we have a nice saturation and never end up with a grey color
    const l = normalizeHash(strHash, typeObj!.lightmin, typeObj!.lightmax); // With a minimum of 70 to make sure we have a nice lightness and never end up with a dark color

    const rgb = hslToRgb(h, s, l);
    const fg = colourIsLight(rgb[0], rgb[1], rgb[2]) ? 'black' : 'white';

    const opacityasHex = colorOpacityMap.get(opacitypct) ?? colorOpacityMap.get(100);

    const fgHex = fg === 'black' ? '#000000' : '#ffffff';
    const bgHex = hslToHex(h, s, l);
    const olHex = hslToHex(h, s, l * 0.5);

    return new ObjectColor(fgHex + opacityasHex, bgHex + opacityasHex, olHex + opacityasHex);
};

/**
 * Generate a heat map from green to red
 * @param index
 * @param total
 * @returns
 */
export const generateHeatMapColor = (index: number, total: number) => {
    const n = (index / total) * 100;
    return `rgb(${(255 * n) / 100}, ${(255 * (100 - n)) / 100}, 0)`;
};

/**
 * We will create a pct % darker color from the hexcolor
 * @param hexcolor
 * @param pct
 * @returns
 */
export const createDarkerColor = (hexcolor: string, pct: number) => {
    let color = hexcolor.replace('#', '');

    let opacityPart = '';
    if (color.length === 8) {
        opacityPart = color.substring(0, 2);
        color = color.substring(0, 6);
    }

    const num = parseInt(color, 16);
    const amt = Math.round(2.55 * pct);
    const R = (num >> 16) - amt;
    const G = ((num >> 8) & 0x00ff) - amt;
    const B = (num & 0x0000ff) - amt;

    return `#${(0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 + (B < 255 ? (B < 1 ? 0 : B) : 255)).toString(16).slice(1)}${opacityPart}`;
};

/**
 * Normalize a hex color so it always starts with #
 * If it is null we will return #000000
 * @param hexcolor
 * @returns
 */
export const normalizeHexColor = (hexcolor: string | null, defaultColor?: string, removeAlpha?: boolean): string | undefined => {
    if (hexcolor == null) {
        if (!defaultColor) {
            return '#000000ff';
        } else {
            return normalizeHexColor(defaultColor);
        }
    } else if (!hexcolor.startsWith('#')) {
        hexcolor = '#' + hexcolor;
    }

    if (hexcolor.length === 7) {
        hexcolor += 'ff';
    }

    if (removeAlpha && hexcolor.length === 9) {
        hexcolor = hexcolor.substring(0, 7);
    }

    return hexcolor.toLowerCase();
};

/**
 * Create a hash from a string
 * @param str
 * @returns
 */
const getHashOfString = (str: string | null) => {
    // We check if the string is a number
    if (!isNaN(Number(str))) {
        // We multiply each digit with 10^index and sum them up
        const digits = str!.split('').map(Number);

        // Move the last digit to the first position
        const lastDigit = digits[digits.length - 1];
        digits.push(lastDigit * 20744533456181232); // Since we often have a id as string we want to make sure that the last digit has a big impact since they usually varey the least

        let newstr = '';
        for (let i = 0; i < digits.length; i++) {
            newstr += (digits[i] * i).toString();
        }
        str = newstr;
    }

    if (str == null) {
        return 0;
    }
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    hash = Math.abs(hash);
    return hash;
};

// const getHashOfStringKnuth = (str: string) => {
//     let hashedValue = 3074457345618258791n;
//     for (let i = 0; i < str.length; i++) {
//         hashedValue += BigInt(str.charCodeAt(i));
//         hashedValue *= 3074457345618258799n;
//     }
//     return hashedValue;
// };

/**
 * This is a implementation of the Knuth hash
 * ulong hashedValue = 3074457345618258791ul;
for (int i = 0; i < read.Length; i++)
{
    hashedValue += read[i];
    hashedValue *= 3074457345618258799ul;
}
return hashedValue;
 * 
 */

/**
 * Normalize a hash to a number between min and max
 * @param hash
 * @param min
 * @param max
 * @returns number
 */
const normalizeHash = (hash: number | bigint, min: number, max: number) => {
    return Math.floor((Number(hash) % (max - min)) + min);
};

/**
 * Convert HSL to Hex
 * @param h
 * @param s
 * @param l
 * @returns
 */
const hslToHex = (h: number, s: number, l: number) => {
    l /= 100;
    const a = (s * Math.min(l, 1 - l)) / 100;
    const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color)
            .toString(16)
            .padStart(2, '0'); // convert to Hex and prefix "0" if needed
    };
    return `#${f(0)}${f(8)}${f(4)}`;
};

/**
 * Convert hue to RGB
 * @param p
 * @param q
 * @param t
 * @returns
 */
const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
};

/**
 * Convert HSL to RGB
 * @param h
 * @param s
 * @param l
 * @returns
 */
const hslToRgb = (h: number, s: number, l: number) => {
    let r, g, b;

    if (s == 0) {
        r = g = b = l; // achromatic
    } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
};

/**
 * Check if a colour is light or dark so we can choose the right text colour for readability
 * @param r
 * @param g
 * @param b
 * @returns
 */
const colourIsLight = (r: number, g: number, b: number) => {
    // Counting the perceptive luminance
    // human eye favors green color...
    const a = 1 - (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return a < 0.5;
};
